import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** REQUIRED — an icon-only control must have an accessible name. */
  label: string;
  icon: ReactNode;
  size?: "sm" | "md";
}

const SIZES = {
  sm: "size-8",
  md: "size-9",
} as const;

/** Square icon-only control. `label` is required and becomes aria-label (a11y, no exceptions). */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, label, icon, size = "md", type, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      title={label}
      className={cn(
        "inline-flex items-center justify-center rounded-(--radius-sm) text-(--text-muted) " +
          "transition-colors duration-(--dur-fast) ease-(--ease-out) " +
          "hover:bg-(--surface-hover) hover:text-(--text) " +
          "active:bg-(--surface-hover) active:brightness-95 " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--brand) " +
          "disabled:opacity-50 disabled:pointer-events-none",
        SIZES[size],
        className,
      )}
      {...props}
    >
      {icon}
    </button>
  );
});
